import React, { ReactNode } from "react"
import {
  Box,
  Divider,
  Flex,
  HStack,
} from "@chakra-ui/react"
import {
  PassBuilderFieldTitle,
  PassBuilderPlatformLabel,
  PassBuilderSectionTitle,
  PassBuilderStatusBadge,
} from "./PassBuilderTypography"
import { AppleIcon, GoogleIcon } from "./PassBuilderPlatformIcons"
import {
  PLATFORM_ICON_LABEL_GAP,
  PLATFORM_PAIR_GAP,
  PLATFORM_SPLIT_GAP,
  PLATFORM_STATUS_GAP,
  SECTION_ABOVE_DIVIDER_SPACING,
  SECTION_BELOW_DIVIDER_SPACING,
  SECTION_DIVIDER_COLOR,
  SECTION_TITLE_MB,
  SECTION_TITLE_MB_PRIMARY,
} from "./passBuilderTokens"

export type PlatformHint = "apple" | "google" | "both"
export type PlatformStatus = "required" | "optional" | "recommended"

type PlatformIconGroupProps = {
  platform: PlatformHint
}

export const PlatformIconGroup = ({ platform }: PlatformIconGroupProps) => (
  <HStack spacing={PLATFORM_PAIR_GAP} display="inline-flex">
    {(platform === "apple" || platform === "both") && (
      <HStack spacing={PLATFORM_ICON_LABEL_GAP}>
        <AppleIcon />
        <PassBuilderPlatformLabel>Apple</PassBuilderPlatformLabel>
      </HStack>
    )}
    {(platform === "google" || platform === "both") && (
      <HStack spacing={PLATFORM_ICON_LABEL_GAP}>
        <GoogleIcon />
        <PassBuilderPlatformLabel>Google</PassBuilderPlatformLabel>
      </HStack>
    )}
  </HStack>
)

type PlatformStatusBadgeProps = {
  status: PlatformStatus
}

const STATUS_TEXT_COLORS: Record<PlatformStatus, string> = {
  required: "red.500",
  optional: "gray.600",
  recommended: "gray.600",
}

const STATUS_LABELS: Record<PlatformStatus, string> = {
  required: "Required",
  optional: "Optional",
  recommended: "Recommended",
}

export const PlatformStatusBadge = ({ status }: PlatformStatusBadgeProps) => (
  <PassBuilderStatusBadge
    color={STATUS_TEXT_COLORS[status]}
    bg="transparent"
    px={0}
  >
    {STATUS_LABELS[status]}
  </PassBuilderStatusBadge>
)

type PassBuilderPlatformRowProps = {
  platform: PlatformHint
  status?: PlatformStatus
  appleStatus?: PlatformStatus
  googleStatus?: PlatformStatus
}

export const PassBuilderPlatformRow = ({
  platform,
  status,
  appleStatus,
  googleStatus,
}: PassBuilderPlatformRowProps) => {
  const split = platform === "both" && appleStatus && googleStatus && appleStatus !== googleStatus

  if (split) {
    return (
      <HStack spacing={PLATFORM_SPLIT_GAP} align="center">
        <HStack spacing={PLATFORM_STATUS_GAP} align="center">
          <PlatformIconGroup platform="apple" />
          <PlatformStatusBadge status={appleStatus} />
        </HStack>
        <HStack spacing={PLATFORM_STATUS_GAP} align="center">
          <PlatformIconGroup platform="google" />
          <PlatformStatusBadge status={googleStatus} />
        </HStack>
      </HStack>
    )
  }

  return (
    <HStack spacing={PLATFORM_STATUS_GAP} align="center">
      <PlatformIconGroup platform={platform} />
      {status && <PlatformStatusBadge status={status} />}
    </HStack>
  )
}

type PassBuilderFieldHeadingProps = {
  title: string
  platform?: PlatformHint
  status?: PlatformStatus
  appleStatus?: PlatformStatus
  googleStatus?: PlatformStatus
  rightElement?: ReactNode
}

export const PassBuilderFieldHeading = ({
  title,
  platform = "both",
  status,
  appleStatus,
  googleStatus,
  rightElement,
}: PassBuilderFieldHeadingProps) => (
  <Box mb={4}>
    <Flex align="center" justify="space-between" mb={2}>
      <PassBuilderFieldTitle>{title}</PassBuilderFieldTitle>
      {rightElement}
    </Flex>
    <PassBuilderPlatformRow
      platform={platform}
      status={status ?? appleStatus ?? googleStatus}
      appleStatus={appleStatus}
      googleStatus={googleStatus}
    />
  </Box>
)

type PassBuilderSectionProps = {
  title: string
  children: ReactNode
  noDivider?: boolean
  /** Branding section uses 30px title; other sections use 24px */
  primaryTitle?: boolean
  /** Space below section title before content (Sketch: 48px branding, 40px colors, 29px others) */
  titleMb?: number | string
}

export const PassBuilderSection = ({
  title,
  children,
  noDivider,
  primaryTitle,
  titleMb,
}: PassBuilderSectionProps) => {
  const resolvedTitleMb = titleMb ?? (primaryTitle ? SECTION_TITLE_MB_PRIMARY : SECTION_TITLE_MB)

  return (
    <Box mb={SECTION_ABOVE_DIVIDER_SPACING} _last={{ mb: 0 }}>
      {!noDivider && (
        <Divider mb={SECTION_BELOW_DIVIDER_SPACING} borderColor={SECTION_DIVIDER_COLOR} />
      )}
      <PassBuilderSectionTitle primary={primaryTitle} mb={resolvedTitleMb}>
        {title}
      </PassBuilderSectionTitle>
      {children}
    </Box>
  )
}
